import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataTable from '../Common/DataTable';
import { api } from '../../services/api';
import {
  extractTenantLocationFilter,
  isTenantDocumentsActionColumn,
} from './reportFieldUtils';

export default function TenantsReport({
  user,
  selectedContractId,
  search,
  tableActionsRef,
  onNavigateToDocuments,
}) {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedContractId || !user) return;

    setColumns([]);
    setData([]);

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.getTenantsReport(
          user.userCode,
          user.type,
          selectedContractId
        );

        setColumns(response.columns || []);
        setData(response.data || []);
      } catch (err) {
        console.error('Error fetching tenants:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedContractId, user]);

  const navigateToDocuments = useCallback((row) => {
    if (!onNavigateToDocuments) return;
    const filter = extractTenantLocationFilter(row, columns);
    if (!filter.building && !filter.apartment) return;
    onNavigateToDocuments(filter);
  }, [columns, onNavigateToDocuments]);

  const handleLinkClick = useCallback((col, row) => {
    if (isTenantDocumentsActionColumn(col)) {
      navigateToDocuments(row);
    }
  }, [navigateToDocuments]);

  const handleActionClick = useCallback((col, row) => {
    if (isTenantDocumentsActionColumn(col)) {
      navigateToDocuments(row);
    }
  }, [navigateToDocuments]);

  const handleRowDoubleClick = useCallback((row) => {
    navigateToDocuments(row);
  }, [navigateToDocuments]);

  const tableColumns = useMemo(
    () => columns.map((col) => (
      isTenantDocumentsActionColumn(col)
        ? { ...col, type: 'action' }
        : col
    )),
    [columns]
  );

  if (loading && columns.length === 0) {
    return (
      <div className="report-panel report-panel--state">
        <div className="report-state-message">טוען...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-panel report-panel--state">
        <div className="report-state-message report-state-message--error">
          שגיאה בטעינת הדוח: {error}
        </div>
      </div>
    );
  }

  if (!columns || columns.length === 0) {
    return (
      <div className="report-panel report-panel--state">
        <div className="report-state-message">אין עמודות להצגה</div>
      </div>
    );
  }

  return (
    <div className="report-panel">
      <DataTable
        ref={tableActionsRef}
        columns={tableColumns}
        data={data}
        search={search}
        onLinkClick={onNavigateToDocuments ? handleLinkClick : undefined}
        onActionClick={onNavigateToDocuments ? handleActionClick : undefined}
        onRowDoubleClick={onNavigateToDocuments ? handleRowDoubleClick : undefined}
      />
    </div>
  );
}
